import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as Diff from 'diff';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import axios from 'axios';

import GetCookie from '../../utils/GetCookie';
import { useTimezone } from '../../utils/TimezoneContext';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import ErrorPopup from '../../utils/popup/ErrorPopup';

import GraphComponent from '../copilotComponents/GraphComponent';
import TableComponent from '../copilotComponents/TableComponent';
import ImageComponent from '../copilotComponents/ImageComponent';
import ReferencesComponent from '../copilotComponents/ReferencesComponent';


const MIN_PANEL_WIDTH = 400;
const MAX_PANEL_WIDTH = 900;
const DEFAULT_PANEL_WIDTH = 500;


const truncate = (str, max = 15) => {
  const s = (str || '').trim();
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + '…';
};

const trimDocTagSpans = (md, maxLen = 15) => {
  if (!md) return md;
  return md.replace(
    /<span([^>]*\b(?:ev-message-card6|ev-message-card7)\b[^>]*)>([\s\S]*?)<\/span>/g,
    (full, attrs, inner) => {
      const rawText = (inner || '').trim();
      if (rawText.includes('<')) return full;
      const shortText = truncate(rawText, maxLen);
      const hasTitle = /\stitle=/.test(attrs);
      const safeTitle = rawText.replace(/"/g, '&quot;');
      const newAttrs = hasTitle ? attrs : `${attrs} title="${safeTitle}"`;
      return `<span${newAttrs}>${shortText}</span>`;
    }
  );
};

const processMarkdownForDisplay = (response) => {
  if (!response || !response.markdown) return '';

  const graphs = response.graphs || [];
  const images = response.images || [];
  const tables = response.tables || [];

  const graphMap = {};
  const imageMap = {};
  const tableMap = {};

  graphs.forEach((g, i) => { graphMap[i + 1] = g.graph_number ?? (i + 1); });
  images.forEach((im, i) => { imageMap[i + 1] = im.img_num ?? (i + 1); });
  tables.forEach((t, i) => { tableMap[i + 1] = t.table_number ?? (i + 1); });

  let md = response.markdown.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

  md = md.replace(/\[(graph|image|table)_(\d+)\]/g, (match, type, idxStr) => {
    const idx = Number(idxStr);
    if (type === 'graph' && graphMap[idx]) return `Figure ${graphMap[idx]}`;
    if (type === 'image' && imageMap[idx]) return `Figure ${imageMap[idx]}`;
    if (type === 'table' && tableMap[idx]) return `Table ${tableMap[idx]}`;
    return match;
  });

  md = trimDocTagSpans(md, 15);
  // Same superscript/subscript conversion as GenbiResponseComponent's display
  md = md.replace(/\^([^\^\s]+)\^/g, '<sup>$1</sup>');
  md = md.replace(/(?<!~)~([^~\s]+)~(?!~)/g, '<sub>$1</sub>');
  return md;
};

const COLOR_SPAN_RE = /<span\s+style="\s*color:\s*([^";]+);?\s*"\s*>([\s\S]*?)<\/span>/g;

const extractColorRuns = (md) => {
  let text = '';
  const colors = [];
  let last = 0;
  const push = (str, color) => {
    text += str;
    for (let i = 0; i < str.length; i++) colors.push(color);
  };
  for (const m of md.matchAll(COLOR_SPAN_RE)) {
    push(md.slice(last, m.index), null);
    push(m[2], m[1].trim());
    last = m.index + m[0].length;
  }
  push(md.slice(last), null);
  return { text, colors };
};

const paintColors = (text, colors, start, end) => {
  let out = '';
  let run = '';
  let runColor = null;
  const flush = () => {
    if (!run) return;
    out += runColor ? `<span style="color:${runColor}">${run}</span>` : run;
    run = '';
  };
  for (let i = start; i < end; i++) {
    const ch = text[i];
    const color = ch === '\n' ? null : colors[i];
    if (color !== runColor) {
      flush();
      runColor = color;
    }
    run += ch;
  }
  flush();
  return out;
};

const CHIP_SPAN_RE = /<span([^>]*\b(ev-message-card(?:6|7|9|10))\b[^>]*)>([\s\S]*?)<\/span>/g;
const OTHER_SPAN_RE = /<span(?!\s+style="\s*color:)[^>]*>[\s\S]*?<\/span>/g;

const chipKey = (attrs, kind, inner) => {
  const title = attrs.match(/\btitle="([^"]*)"/);
  const text = title ? title[1] : inner.replace(/<[^>]*>/g, '');
  return `${kind}|${text.replace(/\s+/g, ' ').trim()}`;
};

const createSpanTokenizer = () => {
  const keyToToken = new Map();
  const tokenFor = (key) => {
    if (!keyToToken.has(key)) {
      keyToToken.set(key, String.fromCharCode(0xE000 + keyToToken.size));
    }
    return keyToToken.get(key);
  };
  const prepare = (md) => {
    const map = {};
    if (!md) return { tokenized: '', map };
    const keep = (token, original) => {
      (map[token] = map[token] || []).push(original);
      return token;
    };
    const tokenized = md
      .replace(CHIP_SPAN_RE, (full, attrs, kind, inner) => keep(tokenFor(chipKey(attrs, kind, inner)), full))
      .replace(OTHER_SPAN_RE, (full) => keep(tokenFor(full), full));
    return { tokenized, map };
  };
  return { prepare };
};

const restoreSpans = (md, map) => {
  const cursor = {};
  return md.replace(/[-]/g, (token) => {
    const originals = map[token];
    if (!originals) return token;
    const i = cursor[token] || 0;
    cursor[token] = i + 1;
    return originals[Math.min(i, originals.length - 1)];
  });
};

const wrapDiffChunk = (value, klass) => {
  if (!value) return '';
  // Split but KEEP the newline separators as their own array entries.
  const segments = value.split(/(\n+)/);
  return segments
    .map(seg => {
      if (!seg) return '';
      if (/^\n+$/.test(seg)) return seg; // raw newlines pass through
      return `<mark class="${klass}">${seg}</mark>`;
    })
    .join('');
};

const WORD_TOKEN_RE = /[-]|\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]/gu;

const tokenizeWords = (text) => {
  const tokens = [];
  const offsets = [];
  for (const m of text.matchAll(WORD_TOKEN_RE)) {
    tokens.push(m[0]);
    offsets.push(m.index);
  }
  offsets.push(text.length);
  return { tokens, offsets };
};

const wordsEqual = (a, b) => a === b || (/^\s+$/.test(a) && /^\s+$/.test(b) && a.includes('\n') === b.includes('\n'));

const computeFlatDiff = (oldMd, newMd) => {
  const { prepare } = createSpanTokenizer();
  const oldTok = prepare(oldMd);
  const newTok = prepare(newMd);
  const oldRuns = extractColorRuns(oldTok.tokenized);
  const newRuns = extractColorRuns(newTok.tokenized);

  if (oldRuns.text === newRuns.text) {
    return {
      left: restoreSpans(paintColors(oldRuns.text, oldRuns.colors, 0, oldRuns.text.length), oldTok.map),
      right: restoreSpans(paintColors(newRuns.text, newRuns.colors, 0, newRuns.text.length), newTok.map),
      identical: true,
    };
  }

  const oldWords = tokenizeWords(oldRuns.text);
  const newWords = tokenizeWords(newRuns.text);
  const changes = Diff.diffArrays(oldWords.tokens, newWords.tokens, { comparator: wordsEqual });

  const leftParts = [];
  const rightParts = [];
  let oldIdx = 0;
  let newIdx = 0;
  const slice = (runs, words, from, count) =>
    paintColors(runs.text, runs.colors, words.offsets[from], words.offsets[from + count]);

  changes.forEach(part => {
    const { count } = part;
    if (part.added) {
      rightParts.push(wrapDiffChunk(slice(newRuns, newWords, newIdx, count), 'diff-word-added'));
      newIdx += count;
    } else if (part.removed) {
      leftParts.push(wrapDiffChunk(slice(oldRuns, oldWords, oldIdx, count), 'diff-word-removed'));
      oldIdx += count;
    } else {
      leftParts.push(slice(oldRuns, oldWords, oldIdx, count));
      rightParts.push(slice(newRuns, newWords, newIdx, count));
      oldIdx += count;
      newIdx += count;
    }
  });

  const left = restoreSpans(leftParts.join(''), oldTok.map);
  const right = restoreSpans(rightParts.join(''), newTok.map);

  return { left, right, identical: false };
};

const markdownParser = unified().use(remarkParse).use(remarkGfm);

const DIFF_CLASS = { added: 'diff-word-added', removed: 'diff-word-removed', modified: 'diff-word-modified' };
const NODE_FORMATS = { strong: 'b', emphasis: 'i', delete: 's', link: 'a', blockquote: 'q', listItem: 'li', tableCell: 'td' };
const HTML_FORMATS = { b: 'b', strong: 'b', i: 'i', em: 'i', u: 'u', s: 's', del: 's', strike: 's', sup: 'sup', sub: 'sub' };
const VISIBLE_TOKEN_RE = /[\p{L}_]+|\p{N}+|[^\s\p{L}\p{N}_]/gu;
const HTML_TAG_RE = /^<\s*(\/)?\s*([a-zA-Z][a-zA-Z0-9]*)([^>]*)>$/;
const CHIP_CLASS_RE = /\b(ev-message-card(?:6|7|9|10))\b/;
const COLOR_STYLE_RE = /\bstyle="\s*color:\s*([^";]+);?\s*"/;

const nodeFormat = (node) => {
  if (node.type === 'heading') return `h${node.depth}`;
  if (node.type === 'list') return node.ordered ? 'ol' : 'ul';
  return NODE_FORMATS[node.type];
};

const formatSignature = (formats) => [...new Set(formats)].sort().join(' ');

const collectSegments = (md) => {
  const segments = [];
  const visit = (node, ctx) => {
    const own = nodeFormat(node);
    const formats = own ? [...ctx.formats, own] : ctx.formats;
    const inLink = ctx.inLink || node.type === 'link';
    const pos = node.position;

    if (node.type === 'text' && pos) {
      segments.push({ kind: 'text', start: pos.start.offset, end: pos.end.offset, fmt: formatSignature(formats), color: ctx.color, inLink });
      return;
    }
    if ((node.type === 'inlineCode' || node.type === 'code') && pos) {
      segments.push({
        kind: 'atom', inline: node.type === 'inlineCode', start: pos.start.offset, end: pos.end.offset,
        fmt: formatSignature(formats), key: `${node.type}|${node.value}`,
      });
      return;
    }
    if (!node.children) return;

    const spanStack = [];
    const htmlFormats = [];
    const children = node.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const tag = child.type === 'html' ? child.value.trim().match(HTML_TAG_RE) : null;
      if (!tag) {
        const color = [...spanStack].reverse().find(s => s.color)?.color || ctx.color;
        visit(child, { formats: [...formats, ...htmlFormats], color, inLink });
        continue;
      }
      const [, closing, rawName, attrs] = tag;
      const name = rawName.toLowerCase();
      if (name === 'span' && !closing) {
        const chip = attrs.match(CHIP_CLASS_RE);
        const closeIdx = chip
          ? children.findIndex((c, j) => j > i && c.type === 'html' && /^<\s*\/\s*span\s*>$/i.test(c.value.trim()))
          : -1;
        if (chip && closeIdx > i && child.position && children[closeIdx].position) {
          const inner = children.slice(i + 1, closeIdx).map(c => c.value || '').join('');
          segments.push({
            kind: 'atom', inline: true, start: child.position.start.offset, end: children[closeIdx].position.end.offset,
            fmt: formatSignature([...formats, ...htmlFormats]), key: chipKey(attrs, chip[1], inner),
          });
          i = closeIdx;
          continue;
        }
        const color = attrs.match(COLOR_STYLE_RE);
        spanStack.push({ color: color ? color[1].trim() : null });
      } else if (name === 'span') {
        spanStack.pop();
      } else if (HTML_FORMATS[name]) {
        if (closing) {
          const idx = htmlFormats.lastIndexOf(HTML_FORMATS[name]);
          if (idx >= 0) htmlFormats.splice(idx, 1);
        } else {
          htmlFormats.push(HTML_FORMATS[name]);
        }
      }
    }
  };
  visit(markdownParser.parse(md), { formats: [], color: null, inLink: false });
  return segments;
};

const segmentTokens = (md, segments) => {
  const tokens = [];
  segments.forEach((seg, segIdx) => {
    if (seg.kind === 'atom') {
      tokens.push({ value: seg.key, start: seg.start, end: seg.end, fmt: seg.fmt, segIdx });
      return;
    }
    const slice = md.slice(seg.start, seg.end);
    for (const m of slice.matchAll(VISIBLE_TOKEN_RE)) {
      const start = seg.start + m.index;
      tokens.push({ value: m[0], start, end: start + m[0].length, fmt: seg.fmt, segIdx });
    }
  });
  return tokens;
};

const escapeLiteralDelimiters = (text) => text.replace(/\\?[*_~]/g, m => (m.length === 2 ? m : `\\${m}`));

const markRun = (body, klass, color) => body
  .split(/(\n+)/)
  .map(part => {
    if (!part || /^\n+$/.test(part)) return part;
    const inner = color ? `<span style="color:${color}">${part}</span>` : part;
    return `<mark class="${klass}">${inner}</mark>`;
  })
  .join('');

const renderLabelled = (md, segments, tokens, labels) => {
  let out = '';
  let pos = 0;
  let t = 0;
  segments.forEach((seg, segIdx) => {
    out += md.slice(pos, seg.start);
    pos = seg.end;
    const first = t;
    while (t < tokens.length && tokens[t].segIdx === segIdx) t++;
    const segLabels = labels.slice(first, t);
    const raw = md.slice(seg.start, seg.end);

    if (!segLabels.some(Boolean)) {
      out += raw;
      return;
    }
    if (seg.kind === 'atom') {
      out += seg.inline ? `<mark class="${DIFF_CLASS[segLabels[0]]}">${raw}</mark>` : raw;
      return;
    }

    const clean = seg.inLink ? (s) => s : escapeLiteralDelimiters;
    let cursor = seg.start;
    for (let i = first; i < t;) {
      const label = labels[i];
      let j = i;
      while (j + 1 < t && labels[j + 1] === label) j++;
      const runStart = tokens[i].start;
      const runEnd = tokens[j].end;
      out += clean(md.slice(cursor, runStart));
      const body = clean(md.slice(runStart, runEnd));
      out += label ? markRun(body, DIFF_CLASS[label], seg.color) : body;
      cursor = runEnd;
      i = j + 1;
    }
    out += clean(md.slice(cursor, seg.end));
  });
  return out + md.slice(pos);
};

const computeStructuredDiff = (oldMd, newMd) => {
  const oldSegments = collectSegments(oldMd);
  const newSegments = collectSegments(newMd);
  const oldTokens = segmentTokens(oldMd, oldSegments);
  const newTokens = segmentTokens(newMd, newSegments);

  const changes = Diff.diffArrays(oldTokens, newTokens, {
    comparator: (a, b) => a.value === b.value,
  });

  const oldLabels = new Array(oldTokens.length).fill(null);
  const newLabels = new Array(newTokens.length).fill(null);
  let oi = 0;
  let ni = 0;
  changes.forEach(part => {
    for (let k = 0; k < part.count; k++) {
      if (part.added) {
        newLabels[ni++] = 'added';
      } else if (part.removed) {
        oldLabels[oi++] = 'removed';
      } else {
        if (oldTokens[oi].fmt !== newTokens[ni].fmt) {
          oldLabels[oi] = 'modified';
          newLabels[ni] = 'modified';
        }
        oi++;
        ni++;
      }
    }
  });

  if (!oldLabels.some(Boolean) && !newLabels.some(Boolean)) {
    return { left: oldMd, right: newMd, identical: true };
  }
  return {
    left: renderLabelled(oldMd, oldSegments, oldTokens, oldLabels),
    right: renderLabelled(newMd, newSegments, newTokens, newLabels),
    identical: false,
  };
};

const computeDiffedMarkdowns = (oldMd, newMd) => {
  if (!oldMd && !newMd) return { left: '', right: '', identical: true };
  if (!oldMd) return { left: '', right: newMd, identical: false };
  if (!newMd) return { left: oldMd, right: '', identical: false };
  if (oldMd === newMd) return { left: oldMd, right: newMd, identical: true };

  try {
    return computeStructuredDiff(oldMd, newMd);
  } catch (e) {
    console.error('Structured version diff failed, falling back to flat diff', e);
    return computeFlatDiff(oldMd, newMd);
  }
};

const hasMeaningfulReferences = (refs) => {
  if (!refs) return false;
  if (Array.isArray(refs)) return refs.length > 0;
  if (typeof refs === 'object') {
    return (refs.doc_ids && refs.doc_ids.length > 0) ||
      (refs.ar_ids && refs.ar_ids.length > 0);
  }
  return false;
};


const normalizeCellText = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
};

const normalizeCaption = (caption) => {
  if (!caption || typeof caption !== 'string') return '';
  return caption.toLowerCase().replace(/\s+/g, ' ').trim();
};

const orderPairs = (pairs) => {
  const matched = pairs.filter(p => p.leftNumber != null && p.rightNumber != null);
  const sortKey = (pair) => {
    if (pair.rightNumber != null) return pair.rightNumber;
    const anchor = matched.reduce(
      (max, m) => (m.leftNumber < pair.leftNumber ? Math.max(max, m.rightNumber) : max),
      0,
    );
    return anchor + 0.5;
  };
  return pairs
    .map(pair => ({ pair, key: sortKey(pair) }))
    .sort((a, b) => a.key - b.key || (a.pair.leftNumber ?? Infinity) - (b.pair.leftNumber ?? Infinity))
    .map(({ pair }) => pair);
};

const INPLACE_FIGURE_SELECTOR = '.genbi-inplace-figure';
const INPLACE_GRAPH_SELECTOR = '.genbi-inplace-graph-portal';
const INPLACE_ASSET_SELECTOR = `${INPLACE_FIGURE_SELECTOR}, ${INPLACE_GRAPH_SELECTOR}`;
const INPLACE_STATUS_CLASS = {
  added: 'agent-version-compare-ipi--added',
  removed: 'agent-version-compare-ipi--removed',
  modified: 'agent-version-compare-ipi--modified',
};
const FIGURE_LABEL_RE = /^\s*Figure\s+\S+\s*$/i;

const buildInPlaceGraphMap = (response) => {
  const map = {};
  (response?.in_place_graphs || []).forEach(ipg => {
    if (ipg?.index != null) map[ipg.index] = ipg;
  });
  return map;
};

const captionLabel = (caption) => {
  const label = caption?.querySelector('b');
  return label && FIGURE_LABEL_RE.test(label.textContent) ? label : null;
};

const captionBodyHtml = (figure) => {
  const caption = figure.querySelector('.genbi-inplace-caption');
  if (!caption) return '';
  const clone = caption.cloneNode(true);
  captionLabel(clone)?.remove();
  return clone.innerHTML.trim();
};

const footerBodyHtml = (figure) => (figure.querySelector('.genbi-inplace-footer')?.innerHTML || '').trim();

const assetSignature = (caption, footer) => [caption, footer]
  .map(html => tokenizeFooter(html || '').map(t => t.key).join(' '))
  .join('|');

const figureSignature = (figure) => assetSignature(captionBodyHtml(figure), footerBodyHtml(figure));

const graphEntry = (portal, ipgMap) => ipgMap[portal.getAttribute('data-ipg-index')] || null;

const graphSignature = (portal, ipgMap) => {
  const ipg = graphEntry(portal, ipgMap);
  return assetSignature(ipg?.subtitle, ipg?.footer);
};

const cellFigures = (cell) => Array.from(cell.querySelectorAll(INPLACE_FIGURE_SELECTOR));
const cellGraphs = (cell) => Array.from(cell.querySelectorAll(INPLACE_GRAPH_SELECTOR));
const hasInPlaceAssets = (cell) => !!cell.querySelector(INPLACE_ASSET_SELECTOR);

const cellTextWithoutFigures = (cell) => {
  const clone = cell.cloneNode(true);
  clone.querySelectorAll(INPLACE_ASSET_SELECTOR).forEach(asset => asset.remove());
  return (clone.textContent || '').replace(/\s+/g, ' ').trim();
};

const tableSignature = (html, ipgMap = {}) => {
  if (!html || !(html.includes('genbi-inplace-figure') || html.includes('genbi-inplace-graph-portal'))) {
    return normalizeCellText(html);
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll(INPLACE_FIGURE_SELECTOR).forEach(fig => {
    fig.replaceWith(document.createTextNode(` [figure ${figureSignature(fig)}] `));
  });
  tmp.querySelectorAll(INPLACE_GRAPH_SELECTOR).forEach(portal => {
    portal.replaceWith(document.createTextNode(` [graph ${graphSignature(portal, ipgMap)}] `));
  });
  return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
};

const markCellTextOutsideFigures = (cell, klass) => {
  const doc = cell.ownerDocument;
  const walker = doc.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent.trim() || node.parentElement?.closest(INPLACE_ASSET_SELECTOR)) continue;
    textNodes.push(node);
  }
  textNodes.forEach(node => {
    const mark = doc.createElement('mark');
    mark.className = klass;
    node.replaceWith(mark);
    mark.appendChild(node);
  });
};

const setCaptionBody = (figure, html) => {
  const caption = figure.querySelector('.genbi-inplace-caption');
  if (!caption) return;
  const label = captionLabel(caption);
  caption.innerHTML = label ? `${label.outerHTML} ${html}` : html;
};

const setFooterBody = (figure, html) => {
  const footer = figure.querySelector('.genbi-inplace-footer');
  if (footer) footer.innerHTML = html;
};

const diffFigurePair = (leftFigure, rightFigure) => {
  const caption = diffFooters(captionBodyHtml(leftFigure), captionBodyHtml(rightFigure));
  const footer = diffFooters(footerBodyHtml(leftFigure), footerBodyHtml(rightFigure));
  if (caption.changed) {
    setCaptionBody(leftFigure, caption.left);
    setCaptionBody(rightFigure, caption.right);
  }
  if (footer.changed) {
    setFooterBody(leftFigure, footer.left);
    setFooterBody(rightFigure, footer.right);
  }
  return caption.changed || footer.changed;
};

const markGraph = (portal, status) => {
  const wrapper = portal.ownerDocument.createElement('div');
  wrapper.className = INPLACE_STATUS_CLASS[status];
  portal.replaceWith(wrapper);
  wrapper.appendChild(portal);
};

const diffGraphPair = (leftPortal, rightPortal, ctx) => {
  const leftIpg = graphEntry(leftPortal, ctx.leftIpgMap);
  const rightIpg = graphEntry(rightPortal, ctx.rightIpgMap);
  const caption = diffFooters(leftIpg?.subtitle || '', rightIpg?.subtitle || '');
  const footer = diffFooters(leftIpg?.footer || '', rightIpg?.footer || '');
  if (!caption.changed && !footer.changed) return false;
  if (leftIpg) {
    ctx.leftIpgOverrides[leftIpg.index] = { ...leftIpg, subtitle: caption.left, footer: footer.left };
  }
  if (rightIpg) {
    ctx.rightIpgOverrides[rightIpg.index] = { ...rightIpg, subtitle: caption.right, footer: footer.right };
  }
  return true;
};

const diffFigureCells = (leftCell, rightCell, ctx) => {
  if (cellTextWithoutFigures(leftCell) !== cellTextWithoutFigures(rightCell)) {
    markCellTextOutsideFigures(leftCell, 'diff-word-removed');
    markCellTextOutsideFigures(rightCell, 'diff-word-added');
  }

  const leftFigures = cellFigures(leftCell);
  const rightFigures = cellFigures(rightCell);
  for (let i = 0; i < Math.max(leftFigures.length, rightFigures.length); i++) {
    const leftFigure = leftFigures[i];
    const rightFigure = rightFigures[i];
    if (leftFigure && !rightFigure) {
      leftFigure.classList.add(INPLACE_STATUS_CLASS.removed);
    } else if (!leftFigure && rightFigure) {
      rightFigure.classList.add(INPLACE_STATUS_CLASS.added);
    } else if (diffFigurePair(leftFigure, rightFigure)) {
      leftFigure.classList.add(INPLACE_STATUS_CLASS.modified);
      rightFigure.classList.add(INPLACE_STATUS_CLASS.modified);
    }
  }

  const leftGraphs = cellGraphs(leftCell);
  const rightGraphs = cellGraphs(rightCell);
  for (let i = 0; i < Math.max(leftGraphs.length, rightGraphs.length); i++) {
    const leftPortal = leftGraphs[i];
    const rightPortal = rightGraphs[i];
    if (leftPortal && !rightPortal) {
      markGraph(leftPortal, 'removed');
    } else if (!leftPortal && rightPortal) {
      markGraph(rightPortal, 'added');
    } else if (diffGraphPair(leftPortal, rightPortal, ctx)) {
      markGraph(leftPortal, 'modified');
      markGraph(rightPortal, 'modified');
    }
  }
};

const pairTablesByCaption = (leftTables = [], rightTables = [], leftIpgMap = {}, rightIpgMap = {}) => {
  const getNum = (t, idx) => t.table_number ?? (idx + 1);

  const leftMatched = new Set();
  const rightMatched = new Set();
  const pairs = [];

  const rightCaptionIndex = new Map();
  rightTables.forEach((t, i) => {
    const cap = normalizeCaption(t.subtitle);
    if (cap && !rightCaptionIndex.has(cap)) {
      rightCaptionIndex.set(cap, i);
    }
  });

  leftTables.forEach((leftTable, leftIdx) => {
    const cap = normalizeCaption(leftTable.subtitle);
    if (!cap) return;
    const rightIdx = rightCaptionIndex.get(cap);
    if (rightIdx === undefined || rightMatched.has(rightIdx)) return;
    const rightTable = rightTables[rightIdx];
    leftMatched.add(leftIdx);
    rightMatched.add(rightIdx);
    pairs.push({
      tableNumber: getNum(rightTable, rightIdx),
      leftNumber: getNum(leftTable, leftIdx),
      rightNumber: getNum(rightTable, rightIdx),
      left: leftTable,
      right: rightTable,
    });
  });

  leftTables.forEach((leftTable, leftIdx) => {
    if (leftMatched.has(leftIdx)) return;
    pairs.push({
      tableNumber: getNum(leftTable, leftIdx),
      leftNumber: getNum(leftTable, leftIdx),
      rightNumber: null,
      left: leftTable,
      right: null,
    });
  });

  rightTables.forEach((rightTable, rightIdx) => {
    if (rightMatched.has(rightIdx)) return;
    pairs.push({
      tableNumber: getNum(rightTable, rightIdx),
      leftNumber: null,
      rightNumber: getNum(rightTable, rightIdx),
      left: null,
      right: rightTable,
    });
  });

  return orderPairs(pairs).map(({ tableNumber, leftNumber, rightNumber, left, right }) => {
    let status;
    if (left && !right) status = 'removed';
    else if (!left && right) status = 'added';
    else {
      const leftText = tableSignature(left.html, leftIpgMap);
      const rightText = tableSignature(right.html, rightIpgMap);
      status = leftText === rightText ? 'unchanged' : 'modified';
    }
    return { tableNumber, leftNumber, rightNumber, left, right, status };
  });
};

const computeTableCellDiff = (leftHtml, rightHtml, leftIpgMap = {}, rightIpgMap = {}) => {
  const ctx = { leftIpgMap, rightIpgMap, leftIpgOverrides: {}, rightIpgOverrides: {} };
  if (!leftHtml || !rightHtml) {
    return { leftHtml: leftHtml || '', rightHtml: rightHtml || '', leftIpgOverrides: {}, rightIpgOverrides: {} };
  }

  const parser = new DOMParser();
  const leftDoc = parser.parseFromString(leftHtml, 'text/html');
  const rightDoc = parser.parseFromString(rightHtml, 'text/html');

  const leftTable = leftDoc.querySelector('table');
  const rightTable = rightDoc.querySelector('table');
  if (!leftTable || !rightTable) {
    return { leftHtml, rightHtml, leftIpgOverrides: {}, rightIpgOverrides: {} };
  }

  const leftRows = Array.from(leftTable.querySelectorAll('tr'));
  const rightRows = Array.from(rightTable.querySelectorAll('tr'));
  const maxRows = Math.max(leftRows.length, rightRows.length);

  const wrapCell = (cell, klass) => {
    if (!cell) return;
    cell.innerHTML = `<mark class="${klass}">${cell.innerHTML}</mark>`;
  };

  for (let r = 0; r < maxRows; r++) {
    const lRow = leftRows[r];
    const rRow = rightRows[r];

    if (lRow && !rRow) {
      lRow.querySelectorAll('td, th').forEach(c => wrapCell(c, 'diff-word-removed'));
      continue;
    }
    if (!lRow && rRow) {
      rRow.querySelectorAll('td, th').forEach(c => wrapCell(c, 'diff-word-added'));
      continue;
    }

    const lCells = Array.from(lRow.querySelectorAll('td, th'));
    const rCells = Array.from(rRow.querySelectorAll('td, th'));
    const maxCells = Math.max(lCells.length, rCells.length);

    for (let c = 0; c < maxCells; c++) {
      const lCell = lCells[c];
      const rCell = rCells[c];

      if (lCell && !rCell) {
        wrapCell(lCell, 'diff-word-removed');
      } else if (!lCell && rCell) {
        wrapCell(rCell, 'diff-word-added');
      } else if (lCell && rCell && (hasInPlaceAssets(lCell) || hasInPlaceAssets(rCell))) {
        diffFigureCells(lCell, rCell, ctx);
      } else if (lCell && rCell) {
        const lText = normalizeCellText(lCell.innerHTML);
        const rText = normalizeCellText(rCell.innerHTML);
        if (lText !== rText) {
          wrapCell(lCell, 'diff-word-removed');
          wrapCell(rCell, 'diff-word-added');
        }
      }
    }
  }

  return {
    leftHtml: leftTable.outerHTML,
    rightHtml: rightTable.outerHTML,
    leftIpgOverrides: ctx.leftIpgOverrides,
    rightIpgOverrides: ctx.rightIpgOverrides,
  };
};


const FOOTER_TOKEN_RE = /<span[^>]*\bev-message-card(?:6|7|9|10)\b[^>]*>[\s\S]*?<\/span>|\[(?:doc|ar)_id_\d+\]|<[^>]*>|[\p{L}_]+|\p{N}+|[^\s\p{L}\p{N}_]/gu;
const SINGLE_CHIP_RE = /^<span([^>]*\b(ev-message-card(?:6|7|9|10))\b[^>]*)>([\s\S]*?)<\/span>$/;

const tokenizeFooter = (html) => {
  const tokens = [];
  if (!html) return tokens;
  for (const m of html.matchAll(FOOTER_TOKEN_RE)) {
    const value = m[0];
    const chip = value.match(SINGLE_CHIP_RE);
    if (!chip && value[0] === '<') continue;
    tokens.push({
      key: chip ? chipKey(chip[1], chip[2], chip[3]) : value,
      start: m.index,
      end: m.index + value.length,
    });
  }
  return tokens;
};

const markFooter = (html, tokens, labels) => {
  let out = '';
  let cursor = 0;
  for (let i = 0; i < tokens.length;) {
    const label = labels[i];
    let j = i;
    while (
      j + 1 < tokens.length &&
      labels[j + 1] === label &&
      !html.slice(tokens[j].end, tokens[j + 1].start).includes('<')
    ) j++;
    const runStart = tokens[i].start;
    const runEnd = tokens[j].end;
    out += html.slice(cursor, runStart);
    const body = html.slice(runStart, runEnd);
    out += label ? `<mark class="${DIFF_CLASS[label]}">${body}</mark>` : body;
    cursor = runEnd;
    i = j + 1;
  }
  return out + html.slice(cursor);
};

const diffFooters = (leftFooter, rightFooter) => {
  const oldTokens = tokenizeFooter(leftFooter);
  const newTokens = tokenizeFooter(rightFooter);
  const changes = Diff.diffArrays(oldTokens, newTokens, {
    comparator: (a, b) => a.key === b.key,
  });

  const oldLabels = new Array(oldTokens.length).fill(null);
  const newLabels = new Array(newTokens.length).fill(null);
  let oi = 0;
  let ni = 0;
  changes.forEach(part => {
    if (part.added) {
      for (let k = 0; k < part.count; k++) newLabels[ni++] = 'added';
    } else if (part.removed) {
      for (let k = 0; k < part.count; k++) oldLabels[oi++] = 'removed';
    } else {
      oi += part.count;
      ni += part.count;
    }
  });

  const changed = oldLabels.some(Boolean) || newLabels.some(Boolean);
  if (!changed) return { left: leftFooter, right: rightFooter, changed: false };
  return {
    left: leftFooter ? markFooter(leftFooter, oldTokens, oldLabels) : leftFooter,
    right: rightFooter ? markFooter(rightFooter, newTokens, newLabels) : rightFooter,
    changed: true,
  };
};

const pairByCaption = (leftItems, rightItems) => {
  const leftMatched = new Set();
  const rightMatched = new Set();
  const pairs = [];

  const rightCaptionIndex = new Map();
  rightItems.forEach((item, i) => {
    const cap = normalizeCaption(item.subtitle);
    if (cap && !rightCaptionIndex.has(cap)) rightCaptionIndex.set(cap, i);
  });

  leftItems.forEach((leftItem, leftIdx) => {
    const cap = normalizeCaption(leftItem.subtitle);
    if (!cap) return;
    const rightIdx = rightCaptionIndex.get(cap);
    if (rightIdx === undefined || rightMatched.has(rightIdx)) return;
    leftMatched.add(leftIdx);
    rightMatched.add(rightIdx);
    pairs.push({ leftNumber: leftItem.figureNumber, rightNumber: rightItems[rightIdx].figureNumber, left: leftItem, right: rightItems[rightIdx] });
  });
  leftItems.forEach((leftItem, leftIdx) => {
    if (leftMatched.has(leftIdx) || normalizeCaption(leftItem.subtitle)) return;
    const rightIdx = rightItems.findIndex((r, i) =>
      !rightMatched.has(i) && !normalizeCaption(r.subtitle) && r.figureNumber === leftItem.figureNumber);
    if (rightIdx < 0) return;
    leftMatched.add(leftIdx);
    rightMatched.add(rightIdx);
    pairs.push({ leftNumber: leftItem.figureNumber, rightNumber: rightItems[rightIdx].figureNumber, left: leftItem, right: rightItems[rightIdx] });
  });
  leftItems.forEach((leftItem, leftIdx) => {
    if (!leftMatched.has(leftIdx)) pairs.push({ leftNumber: leftItem.figureNumber, rightNumber: null, left: leftItem, right: null });
  });
  rightItems.forEach((rightItem, rightIdx) => {
    if (!rightMatched.has(rightIdx)) pairs.push({ leftNumber: null, rightNumber: rightItem.figureNumber, left: null, right: rightItem });
  });

  return orderPairs(pairs);
};

const withFooter = (item, footer) => (
  'footer' in item || footer ? { ...item, footer } : item
);

const annotateFigures = (leftItems, rightItems) => {
  const leftAnnotated = [];
  const rightAnnotated = [];
  let allUnchanged = true;

  pairByCaption(leftItems, rightItems).forEach(({ left, right }) => {
    if (left && !right) {
      allUnchanged = false;
      leftAnnotated.push({ status: 'removed', item: left, badgeLabel: 'Removed in new version' });
      return;
    }
    if (!left && right) {
      allUnchanged = false;
      rightAnnotated.push({ status: 'added', item: right, badgeLabel: 'Added in new version' });
      return;
    }
    const footerDiff = diffFooters(left.footer || '', right.footer || '');
    const modified = footerDiff.changed || (left.subtitle || '') !== (right.subtitle || '');
    if (!modified) {
      leftAnnotated.push({ status: 'unchanged', item: left, badgeLabel: null });
      rightAnnotated.push({ status: 'unchanged', item: right, badgeLabel: null });
      return;
    }
    allUnchanged = false;
    leftAnnotated.push({ status: 'modified', item: withFooter(left, footerDiff.left), badgeLabel: 'Modified' });
    rightAnnotated.push({ status: 'modified', item: withFooter(right, footerDiff.right), badgeLabel: 'Modified' });
  });

  return { leftAnnotated, rightAnnotated, allUnchanged };
};

const numberFigures = (items, numberKey) => (items || []).map((item, i) => ({
  ...item, figureNumber: item[numberKey] ?? (i + 1),
}));

const ASSET_STATUS_STYLE = {
  added: { wrapper: 'agent-version-compare-asset--added pb-0', badge: 'agent-version-compare-asset-badge--added', icon: 'bi-plus-circle' },
  removed: { wrapper: 'agent-version-compare-asset--removed pb-0', badge: 'agent-version-compare-asset-badge--removed', icon: 'bi-dash-circle' },
  modified: { wrapper: 'agent-version-compare-asset--modified pb-0', badge: 'agent-version-compare-asset-badge--modified', icon: 'bi-pencil-square' },
};

const AssetChangeFrame = ({ status, badgeLabel, children }) => {
  const style = ASSET_STATUS_STYLE[status];
  return (
    <div className={style ? `agent-version-compare-asset ${style.wrapper}` : 'agent-version-compare-asset'}>
      {style && (
        <span className={`agent-version-compare-asset-badge ${style.badge}`}>
          <i className={`bi ${style.icon} me-1`}></i>
          {badgeLabel}
        </span>
      )}
      {children}
    </div>
  );
};

const MARKDOWN_COMPONENTS = {
  h1: ({ children }) => <div className="ev-med-p-blue mb-2">{children}</div>,
  h2: ({ children }) => <div className="ev-reg-p-dark mb-1 fw-bold">{children}</div>,
  h3: ({ children }) => <div className="ev-reg-p-dark mb-1 fw-bold">{children}</div>,
  h4: ({ children }) => <div className="ev-small-p-blue mb-1 fw-bold">{children}</div>,
  h5: ({ children }) => <div className="ev-small-p-blue mb-1 fw-bold">{children}</div>,
  h6: ({ children }) => <div className="ev-small-p-blue mb-1 fw-bold">{children}</div>,
};

const VersionMetadataStrip = ({ payload }) => {
  const { formatDateTime } = useTimezone();
  if (!payload) {
    return (
      <div className="agent-version-compare-meta-half">
        <span className="ev-xsmall-p-dark" style={{ opacity: 0.5 }}>—</span>
      </div>
    );
  }
  const { version, source, timestamp } = payload;
  return (
    <div className="agent-version-compare-meta-half d-flex align-items-center gap-2 flex-wrap">
      <span className="ev-bold-med ev-reg-p-dark-blue">
        Version {version}
      </span>
      {source && (
        <span className={`ev-btn-xsmall ${source === 'manual' ? 'genbi-source-manual' : 'genbi-source-ai'}`}>
          {source === 'manual' ? (
            <><i className="bi bi-person-fill me-1 ev-bold-reg"></i>Manual</>
          ) : source === 'formatting' ? (
            <><i className="bi bi-spellcheck me-1 ev-bold-reg"></i>Formatting</>
          ) : (
            <><i className="bi bi-stars me-1 ev-bold-reg"></i>AI</>
          )}
        </span>
      )}
      {timestamp && (
        <span className="agent-version-compare-timestamp ev-xsmall-p-dark">
          {formatDateTime(timestamp)}
        </span>
      )}
    </div>
  );
};

const VersionColumn = ({ payload, annotatedMarkdown, annotatedTables, annotatedGraphs = [], annotatedImages = [], inPlaceGraphOverrides = {} }) => {
  if (!payload || !payload.response) {
    return (
      <div className="agent-version-compare-column d-flex align-items-center justify-content-center">
        <span className="ev-xsmall-p-dark" style={{ opacity: 0.5 }}>No data</span>
      </div>
    );
  }

  const { response } = payload;
  const inPlaceGraphMap = { ...buildInPlaceGraphMap(response), ...inPlaceGraphOverrides };
  const refs = response.references;
  const showRefs = hasMeaningfulReferences(refs);
  const hasTables = annotatedTables && annotatedTables.length > 0;

  return (
    <div className="agent-version-compare-column scrollable-element" style={{ minWidth: 0, overflowX: 'hidden' }}>
      {annotatedMarkdown && (
        <div className="markdown-justified mb-3 ev-small-p-dark p-3">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={MARKDOWN_COMPONENTS}
          >
            {annotatedMarkdown}
          </ReactMarkdown>
        </div>
      )}

      {annotatedGraphs.length > 0 && (
        <div className="mb-3 px-3">
          {annotatedGraphs.map(({ status, item: graph, badgeLabel }) => (
            <AssetChangeFrame key={`${graph.graph_id || graph.figureNumber}-${status}`} status={status} badgeLabel={badgeLabel}>
              <GraphComponent
                graphData={graph}
                isLoading={false}
                contentId={graph.figureNumber}
                subtitle={graph.subtitle}
                footer={'footer' in graph ? graph.footer : null}
              />
            </AssetChangeFrame>
          ))}
        </div>
      )}

      {annotatedImages.length > 0 && (
        <div className="mb-3 px-3">
          {annotatedImages.map(({ status, item: image, badgeLabel }) => (
            <AssetChangeFrame key={`${image.image_id || image.figureNumber}-${status}`} status={status} badgeLabel={badgeLabel}>
              <ImageComponent
                imageData={image}
                isLoading={false}
                contentId={image.figureNumber}
                subtitle={image.subtitle}
                footer={'footer' in image ? image.footer : null}
              />
            </AssetChangeFrame>
          ))}
        </div>
      )}

      {hasTables && (
        <div className="mb-3 px-3">
          {annotatedTables.map(({ status, tableData, tableNumber, badgeLabel }) => (
            <AssetChangeFrame key={`${tableNumber}-${status}`} status={status} badgeLabel={badgeLabel}>
              <TableComponent
                tableData={tableData}
                isLoading={false}
                contentId={tableNumber}
                subtitle={tableData.subtitle}
                inPlaceGraphMap={inPlaceGraphMap}
              />
            </AssetChangeFrame>
          ))}
        </div>
      )}

      {showRefs && (
        <div className="px-3 pb-3" style={{ minWidth: 0, overflow: 'hidden' }}>
          <ReferencesComponent references={refs} />
        </div>
      )}

      {!annotatedMarkdown && annotatedGraphs.length === 0 && annotatedImages.length === 0 && !hasTables && !showRefs && (
        <div className="text-center p-4">
          <span className="ev-small-p-dark" style={{ opacity: 0.5 }}>
            No content in this version.
          </span>
        </div>
      )}
    </div>
  );
};

const AgentVersionComparePanel = ({ agentId, versionA, versionB, fabricId, onClose }) => {
  const [data, setData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('Loading version comparison...');
  const [errorMessage, setErrorMessage] = useState('');

  // ── Panel width drag state (left-edge resize) ──
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const isDraggingWidth = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const panelRef = useRef(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const fetchComparison = async () => {
      setIsProcessing(true);
      setProcessingMessage('Loading version comparison...');
      try {
        const API_BASE_URL = '/pceapi/genbi';
        const headers = {
          Authorization: GetCookie('access_token'),
        };

        const response = await axios.post(
          `${API_BASE_URL}/compare-agent-versions`,
          {
            agent_id: agentId,
            version_a: versionA,
            version_b: versionB,
            fabric_id: fabricId,
          },
          { headers }
        );
        if (cancelled) return;
        if (response.data.status === 'success' && response.data.data) {
          setData(response.data.data);
        } else {
          setErrorMessage(response.data.message || 'Failed to load version comparison.');
        }
      } catch (e) {
        if (!cancelled) setErrorMessage('Network error: ' + e.message);
      } finally {
        if (!cancelled) setIsProcessing(false);
      }
    };
    fetchComparison();
    return () => { cancelled = true; };
  }, [agentId, versionA, versionB, fabricId]);

  // ── ESC to close ──
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current && onCloseRef.current();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // ── Click outside to close ──
  // useEffect(() => {
  //   const handler = (e) => {
  //     if (!document.contains(e.target)) return;
  //     if (e.target.closest('.modal') || e.target.closest('.react-doc-viewer')) return;
  //     if (panelRef.current && !panelRef.current.contains(e.target)) {
  //       onCloseRef.current && onCloseRef.current();
  //     }
  //   };
  //   document.addEventListener('mousedown', handler);
  //   return () => document.removeEventListener('mousedown', handler);
  // }, []);

  // ── Left-edge drag resize ──
  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingWidth.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingWidth.current) return;
      // Dragging left increases width (panel is right-anchored)
      const delta = dragStartX.current - e.clientX;
      const newW = Math.min(
        MAX_PANEL_WIDTH,
        Math.max(MIN_PANEL_WIDTH, dragStartWidth.current + delta)
      );
      setPanelWidth(newW);
    };
    const onUp = () => {
      if (!isDraggingWidth.current) return;
      isDraggingWidth.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const closeErrorPopup = () => {
    setErrorMessage('');
    onClose();
  };

  // ── Defensive ordering: older on left, newer on right ──
  let leftPayload = data?.version_a || null;
  let rightPayload = data?.version_b || null;
  if (
    leftPayload && rightPayload &&
    typeof leftPayload.version === 'number' &&
    typeof rightPayload.version === 'number' &&
    leftPayload.version > rightPayload.version
  ) {
    [leftPayload, rightPayload] = [rightPayload, leftPayload];
  }

  const { left: leftMd, right: rightMd, identical: isTextIdentical } = useMemo(() => {
    if (!leftPayload && !rightPayload) return { left: '', right: '', identical: true };
    const oldMd = processMarkdownForDisplay(leftPayload?.response);
    const newMd = processMarkdownForDisplay(rightPayload?.response);
    return computeDiffedMarkdowns(oldMd, newMd);
  }, [leftPayload, rightPayload]);

  const { leftAnnotatedTables, rightAnnotatedTables, allTablesUnchanged, leftIpgOverrides, rightIpgOverrides } = useMemo(() => {
    const leftTables = (leftPayload?.response?.tables || []).map(t => ({
      ...t,
      html: t.html ? trimDocTagSpans(t.html, 15) : t.html,
    }));
    const rightTables = (rightPayload?.response?.tables || []).map(t => ({
      ...t,
      html: t.html ? trimDocTagSpans(t.html, 15) : t.html,
    }));

    const leftIpgMap = buildInPlaceGraphMap(leftPayload?.response);
    const rightIpgMap = buildInPlaceGraphMap(rightPayload?.response);
    const pairs = pairTablesByCaption(leftTables, rightTables, leftIpgMap, rightIpgMap);
    const leftOverrides = {};
    const rightOverrides = {};

    const leftAnnotated = [];
    const rightAnnotated = [];
    let allUnchanged = true;

    pairs.forEach(({ leftNumber, rightNumber, left, right, status }) => {
      if (status !== 'unchanged') allUnchanged = false;

      if (status === 'unchanged') {
        leftAnnotated.push({ status, tableNumber: leftNumber, tableData: left, badgeLabel: null });
        rightAnnotated.push({ status, tableNumber: rightNumber, tableData: right, badgeLabel: null });
      } else if (status === 'removed') {
        leftAnnotated.push({
          status, tableNumber: leftNumber, tableData: left,
          badgeLabel: 'Removed in new version',
        });
      } else if (status === 'added') {
        rightAnnotated.push({
          status, tableNumber: rightNumber, tableData: right,
          badgeLabel: 'Added in new version',
        });
      } else if (status === 'modified') {
        const { leftHtml, rightHtml, leftIpgOverrides: lOv, rightIpgOverrides: rOv } =
          computeTableCellDiff(left.html, right.html, leftIpgMap, rightIpgMap);
        Object.assign(leftOverrides, lOv);
        Object.assign(rightOverrides, rOv);
        leftAnnotated.push({
          status, tableNumber: leftNumber,
          tableData: { ...left, html: leftHtml },
          badgeLabel: 'Modified',
        });
        rightAnnotated.push({
          status, tableNumber: rightNumber,
          tableData: { ...right, html: rightHtml },
          badgeLabel: 'Modified',
        });
      }
    });

    return {
      leftAnnotatedTables: leftAnnotated,
      rightAnnotatedTables: rightAnnotated,
      allTablesUnchanged: allUnchanged,
      leftIpgOverrides: leftOverrides,
      rightIpgOverrides: rightOverrides,
    };
  }, [leftPayload, rightPayload]);

  const figureDiff = useMemo(() => {
    const graphs = annotateFigures(
      numberFigures(leftPayload?.response?.graphs, 'graph_number'),
      numberFigures(rightPayload?.response?.graphs, 'graph_number'),
    );
    const images = annotateFigures(
      numberFigures(leftPayload?.response?.images, 'img_num'),
      numberFigures(rightPayload?.response?.images, 'img_num'),
    );
    return { graphs, images, allUnchanged: graphs.allUnchanged && images.allUnchanged };
  }, [leftPayload, rightPayload]);

  const allAssetsUnchanged = allTablesUnchanged && figureDiff.allUnchanged;

  return (
    <>
      {isProcessing && <ProcessingPopup message={processingMessage} />}
      {errorMessage && <ErrorPopup message={errorMessage} onClose={closeErrorPopup} />}

      <div
        ref={panelRef}
        className="agent-version-compare-panel position-absolute ev-message-card2 ev-card-3 ev-radius-reg shadow d-flex flex-column vh-75"
        style={{ width: panelWidth }}
        role="dialog"
        aria-label="Agent version comparison"
      >
        {/* ── Left-edge drag handle ── */}
        <div
          className="agent-version-compare-drag-handle"
          onMouseDown={handleDragStart}
          title="Drag to resize"
        >
          <div className="agent-version-compare-drag-bar" />
        </div>

        {/* ── Header ── */}
        <div className="agent-version-compare-header d-flex align-items-center justify-content-between flex-shrink-0 gap-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <i className="bi bi-files ev-small-p-blue"></i>
            <span className="ev-bold-med ev-reg-p-dark">Version Comparison</span>
            {leftPayload && rightPayload && (
              <span className="ev-message-card1 ev-btn-xsmall ms-1">
                <i className="bi bi-arrow-left-right me-1"></i>
                v{leftPayload.version}&nbsp;vs&nbsp;v{rightPayload.version}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            {data && (!isTextIdentical || !allAssetsUnchanged) && (
              <div className="d-flex align-items-center justify-content-end flex-wrap gap-2 me-2">
                <span className="d-inline-flex align-items-center gap-1">
                  <span className="agent-version-compare-legend agent-version-compare-legend--removed"></span>
                  <span className="ev-xsmall-p-dark">Removed</span>
                </span>
                <span className="d-inline-flex align-items-center gap-1">
                  <span className="agent-version-compare-legend agent-version-compare-legend--added"></span>
                  <span className="ev-xsmall-p-dark">Added</span>
                </span>
                <span className="d-inline-flex align-items-center gap-1">
                  <span className="agent-version-compare-legend agent-version-compare-legend--modified"></span>
                  <span className="ev-xsmall-p-dark">Modified</span>
                </span>
              </div>
            )}
            <button
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall ev-radius-xlarge flex-shrink-0"
              onClick={onClose}
              aria-label="Close comparison"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        {/* ── Metadata stripe ── */}
        <div className="agent-version-compare-meta-bar d-flex flex-shrink-0">
          <VersionMetadataStrip payload={leftPayload} />
          <div className="agent-version-compare-meta-divider" />
          <VersionMetadataStrip payload={rightPayload} />
        </div>

        {/* ── Identical banner ── */}
        {data && isTextIdentical && allAssetsUnchanged && (
          <div className="agent-version-compare-identical flex-shrink-0">
            <i className="bi bi-check2-circle me-2"></i>
            Content is identical between these versions.
          </div>
        )}

        {/* ── Side-by-side columns ── */}
        <div className="agent-version-compare-columns flex-grow-1">
          {!data && !isProcessing && !errorMessage && (
            <div className="d-flex align-items-center justify-content-center w-100 p-4">
              <span className="ev-small-p-dark" style={{ opacity: 0.5 }}>Loading…</span>
            </div>
          )}
          {data && (
            <>
              <VersionColumn
                payload={leftPayload}
                annotatedMarkdown={leftMd}
                annotatedTables={leftAnnotatedTables}
                annotatedGraphs={figureDiff.graphs.leftAnnotated}
                annotatedImages={figureDiff.images.leftAnnotated}
                inPlaceGraphOverrides={leftIpgOverrides}
              />
              <div className="agent-version-compare-column-divider" />
              <VersionColumn
                payload={rightPayload}
                annotatedMarkdown={rightMd}
                annotatedTables={rightAnnotatedTables}
                annotatedGraphs={figureDiff.graphs.rightAnnotated}
                annotatedImages={figureDiff.images.rightAnnotated}
                inPlaceGraphOverrides={rightIpgOverrides}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AgentVersionComparePanel;